import {
  Box,
  Button,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AdminSubscriptionRow, OfferView, PendingSubscriptionRow } from '@/api/generated';
import { ApiErrorState } from '@/components/ApiErrorState';
import { EmptyState } from '@/components/EmptyState';
import { StatusChip } from '@/components/status/StatusChip';
import { formatCivilOrDash } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useTenantCurrency, useTenantLink } from '@/tenant/useTenant';
import { resolveTokens } from '@/theme';
import { offerById, offerLabel, offerPeriodLabel } from './offerLabel';
import {
  STATUS_FILTERS,
  deriveStatus,
  matchesFilter,
  type StatusFilter,
} from './subscriberStatus';

export interface SubscriberListProps {
  subscriptions: AdminSubscriptionRow[];
  pending: PendingSubscriptionRow[];
  offers: OfferView[];
  /** Set when the offers request failed. The list still renders; the offer names can't. */
  offersError?: unknown;
  onRetryOffers?: () => void;
  filter: StatusFilter;
  query: string;
  selectedId: number | undefined;
  onFilter: (filter: StatusFilter) => void;
  onQuery: (query: string) => void;
  onInvite: () => void;
  onSubscribe: () => void;
  onComp: () => void;
  showToolbarActions: boolean;
  /** The person may invite: the "none yet" empty state offers it. */
  canWrite: boolean;
  /** A search or a status filter is on (an empty list is then "no match"). */
  filtered: boolean;
  onClearFilters: () => void;
}

/** The list shows at most this many rows; past it, a scope line asks for a search or filter. */
const LIST_LIMIT = 200;

function matchesSearch(
  query: string,
  email: string | undefined,
  displayName: string | undefined,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q || q.includes('@')) return true;
  return (email ?? '').toLowerCase().includes(q) || (displayName ?? '').toLowerCase().includes(q);
}

export function SubscriberList({
  subscriptions,
  pending,
  offers,
  offersError = null,
  onRetryOffers,
  filter,
  query,
  selectedId,
  onFilter,
  onQuery,
  onInvite,
  onSubscribe,
  onComp,
  showToolbarActions,
  canWrite,
  filtered,
  onClearFilters,
}: SubscriberListProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const link = useTenantLink();
  // A record's link keeps the list's query, so Close returns to the same list (TMM-B6).
  const { search } = useLocation();
  const tokens = resolveTokens(useTheme());
  const currency = useTenantCurrency();
  const now = new Date();
  const unavailable = t('subscribers.unavailable');

  const visibleSubs = subscriptions.filter((row) => {
    const derived = deriveStatus(row, now);
    return matchesFilter(derived, filter) && matchesSearch(query, row.email, row.displayName);
  });
  const visiblePending =
    filter === 'all' ? pending.filter((row) => matchesSearch(query, row.email, undefined)) : [];
  const total = visibleSubs.length + visiblePending.length;
  const rawTotal = subscriptions.length + pending.length;

  return (
    <Box>
      <Stack spacing={1.5} sx={{ mb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          label={t('subscribers.search')}
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
        <Box>
          {/* Single-select; picking the current value keeps it, so one is always on. */}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filter}
            aria-label={t('subscribers.filter.label')}
            onChange={(_, next: StatusFilter | null) => {
              if (next !== null) onFilter(next);
            }}
            sx={{ flexWrap: 'wrap' }}
          >
            {STATUS_FILTERS.map((key) => (
              <ToggleButton key={key} value={key}>
                {t(`subscribers.filter.${key === 'past_due' ? 'pastDue' : key}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        {offersError != null && onRetryOffers ? (
          <ApiErrorState error={offersError} onRetry={onRetryOffers} />
        ) : null}
        {showToolbarActions ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button onClick={onInvite}>{t('subscribers.invite')}</Button>
            <Button onClick={onSubscribe}>{t('subscribers.subscribeWithCard')}</Button>
            <Button onClick={onComp}>{t('subscribers.comp')}</Button>
          </Stack>
        ) : null}
      </Stack>
      {rawTotal > LIST_LIMIT ? (
        <Typography variant="body2" sx={{ color: tokens.text2, mb: 1 }}>
          {t('subscribers.scope', { n: LIST_LIMIT })}
        </Typography>
      ) : null}
      {total === 0 && filtered ? (
        <EmptyState kind="noMatch" title={t('subscribers.noMatch')} onClear={onClearFilters} />
      ) : null}
      {total === 0 && !filtered ? (
        <EmptyState
          kind="none"
          title={t('subscribers.empty')}
          action={
            canWrite ? (
              <Button variant="contained" onClick={onInvite}>
                {t('subscribers.inviteFirst')}
              </Button>
            ) : undefined
          }
        />
      ) : null}
      {total === 0 ? null : (
        <Table size="small" aria-label={t('pages.subscribers.title')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('subscribers.col.email')}</TableCell>
              <TableCell>{t('subscribers.col.status')}</TableCell>
              <TableCell>{t('subscribers.col.interval')}</TableCell>
              <TableCell>{t('subscribers.col.offer')}</TableCell>
              <TableCell>{t('subscribers.col.since')}</TableCell>
              <TableCell>{t('subscribers.col.renewal')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* A pending row has no interval or renewal yet: those cells stay empty (DATA-001). */}
            {visiblePending.map((row) => (
              <TableRow key={`pending-${row.id ?? row.email}`}>
                <TableCell component="th" scope="row">
                  {row.email ?? unavailable}
                </TableCell>
                <TableCell>
                  <StatusChip domain="subscriberPending" status={row.status} />
                </TableCell>
                <TableCell />
                <TableCell>
                  {offerLabel(
                    offerById(offers, row.retailOfferId),
                    currency,
                    locale,
                    offerPeriodLabel(
                      offerById(offers, row.retailOfferId)?.interval,
                      t('dashboard.perMonth'),
                      t('dashboard.perYear'),
                    ),
                    unavailable,
                  )}
                </TableCell>
                <TableCell>{formatCivilOrDash(row.createdAt, locale, unavailable)}</TableCell>
                <TableCell />
              </TableRow>
            ))}
            {visibleSubs.map((row) => {
              const derived = deriveStatus(row, now);
              const selected = row.id !== undefined && row.id === selectedId;
              const interval =
                row.interval === 'YEAR' || row.interval === 'MONTH' ? row.interval : undefined;
              return (
                <TableRow key={row.id ?? row.email} hover selected={selected}>
                  <TableCell component="th" scope="row">
                    <Link
                      component={RouterLink}
                      to={{ pathname: link(`subscribers/${row.id}`), search }}
                      aria-current={selected ? 'true' : undefined}
                    >
                      {row.email ?? unavailable}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <StatusChip domain="subscriber" status={derived} />
                  </TableCell>
                  <TableCell>
                    {interval ? (
                      <Chip size="small" label={t(`pricing.interval.${interval}`)} />
                    ) : (
                      unavailable
                    )}
                  </TableCell>
                  <TableCell>
                    {offerLabel(
                      offerById(offers, row.retailOfferId),
                      currency,
                      locale,
                      offerPeriodLabel(
                        offerById(offers, row.retailOfferId)?.interval ?? row.interval,
                        t('dashboard.perMonth'),
                        t('dashboard.perYear'),
                      ),
                      unavailable,
                    )}
                  </TableCell>
                  <TableCell>{formatCivilOrDash(row.createdAt, locale, unavailable)}</TableCell>
                  <TableCell>{formatCivilOrDash(row.nextBillingOn, locale, unavailable)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <Typography variant="body2" sx={{ color: tokens.text2, mt: 1.5 }}>
        {t('subscribers.count', { n: total })}
      </Typography>
    </Box>
  );
}
